import type Luminary from '@lumen5/luminary';
import type { Scene } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

// A locked render queue (the editor drawing its live preview) means an enqueued render never
// starts; give up after this so a capture fails fast instead of hanging the turn.
const SCENE_RENDER_TIMEOUT_MS = 15000;

// The model downscales images and bills by pixel area, so cap the captured frame's long edge:
// 'low' is a cheap overview (the scene's text is already in the agent's text context); 'high' is
// for reading fine detail, e.g. the agent reviewing its own rendered composition. At 'low' a 1920
// canvas lands at 512x288, where the smallest text the lint even permits (2.5% of canvas height)
// renders about 7px tall — enough to judge layout and colour, not to read.
export const SCREENSHOT_MAX_EDGE = { low: 512, high: 1024 } as const;

// Captured frames are JPEG, not PNG to help keep the image size small.
const FRAME_IMAGE_TYPE = 'image/jpeg';
const FRAME_IMAGE_QUALITY = 0.82;

/** Encode a rendered frame for the agent. Centralised so every capture path shares the encoding. */
function encodeFrame(canvas: HTMLCanvasElement): string {
    return canvas.toDataURL(FRAME_IMAGE_TYPE, FRAME_IMAGE_QUALITY);
}

// Scene-progress points (0..1) each multi-frame "moment" renders. Several frames per moment so the
// sequence conveys motion — an entrance/exit isn't visible in a single still.
//
// Nothing samples exactly 0: at the very first instant no entrance has run, so the frame is blank by
// construction and carries no information. Agents read that blankness as a renderer fault rather
// than as their own timeline — one rationalised it in its closing summary, another altered a working
// design to "fix" it — so the first sample sits just past the start instead.
export const MOMENT_PROGRESS = {
    intro: [0.05, 0.15, 0.35],
    outro: [0.65, 0.85, 1],
    full: [0.05, 0.25, 0.5, 0.75, 1],
} as const;

/**
 * Render a scene's preview frame to a data URL, waiting for assets to settle first. Backs the
 * capture_scene tool's default single still. Uses the queue-based preview render, which works even while
 * the editor preview is playing. Rejects on timeout/failure.
 */
export async function captureSceneScreenshot(
    luminary: Luminary,
    scene: Scene,
    maxEdge: number = SCREENSHOT_MAX_EDGE.low,
): Promise<string> {
    await luminary.onAssetsDoneTryingToLoad();

    const canvas = createRenderCanvas(luminary, maxEdge);
    const abortController = new AbortController();
    const timeout = setTimeout(() => abortController.abort(), SCENE_RENDER_TIMEOUT_MS);
    try {
        await scene.renderPreviewToCanvas(canvas, {
            subsceneIndex: 0,
            abortSignal: abortController.signal,
        });
        return encodeFrame(canvas);
    } finally {
        clearTimeout(timeout);
        canvas.remove();
    }
}

/**
 * Render the scene at each normalized progress point (0..1) so the agent can see motion — the
 * scene's animations and its own composition, which a single still can't convey. Returns one entry
 * per point (a percent label + data URL).
 */
export async function captureSceneFrames(
    luminary: Luminary,
    scene: Scene,
    progressPoints: readonly number[],
    maxEdge: number = SCREENSHOT_MAX_EDGE.low,
): Promise<Array<{ label: string; dataUrl: string }>> {
    await luminary.onAssetsDoneTryingToLoad();
    // renderAtTimeToCanvas throws while the ticker is running; pause so the render queue processes.
    if (luminary.isPlaying()) {
        luminary.pause();
    }

    const sceneIndex = luminary.scenes.findIndex((s) => s.id === scene.id);
    const slice = luminary.getTimeSlices()[sceneIndex];
    if (!slice) {
        return [];
    }
    const [startMs, endMs] = slice;

    // Reuse one canvas for every frame — renderAtTimeToCanvas clears and redraws it each call.
    const canvas = createRenderCanvas(luminary, maxEdge);
    const frames: Array<{ label: string; dataUrl: string }> = [];
    try {
        for (const progress of progressPoints) {
            // Stay just inside the window so progress 1 doesn't spill into the next scene's first frame.
            const time = Math.min(startMs + progress * (endMs - startMs), endMs - 1);
            await withTimeout(luminary.renderAtTimeToCanvas(time, canvas), SCENE_RENDER_TIMEOUT_MS);
            frames.push({
                label: `${Math.round(progress * 100)}%`,
                dataUrl: encodeFrame(canvas),
            });
        }
    } finally {
        canvas.remove();
    }
    return frames;
}

/**
 * A render target sized so its long edge is at most `maxEdge` (never upscaled), preserving aspect.
 * Luminary scales the full render into whatever size we give it, so this is the capped output — no
 * separate downscale pass needed.
 */
function createRenderCanvas(luminary: Luminary, maxEdge: number): HTMLCanvasElement {
    const { width, height } = VideoData.getResolution(
        luminary.resolutionValue,
        luminary.aspectRatio,
    );
    const scale = Math.min(1, maxEdge / Math.max(width, height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(width * scale);
    canvas.height = Math.round(height * scale);
    return canvas;
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
    return Promise.race([
        promise,
        new Promise<T>((_resolve, reject) => {
            setTimeout(() => reject(new Error('scene render timed out')), ms);
        }),
    ]);
}
